import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Download, ExternalLink, FileText, LoaderCircle, X } from 'lucide-react';
import { getResourceFileUrl } from '../services/api';

const PdfViewerModal = ({ resource, onClose }) => {
  const [previewStatus, setPreviewStatus] = useState('loading');
  const viewUrl = resource?._id ? getResourceFileUrl(resource._id) : '';
  const downloadUrl = resource?._id ? getResourceFileUrl(resource._id, true) : '';

  useEffect(() => {
    if (!resource) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [resource, onClose]);

  useEffect(() => {
    if (!resource?.fileUrl || !viewUrl) return undefined;

    const controller = new AbortController();
    setPreviewStatus('loading');

    fetch(viewUrl, { method: 'HEAD', signal: controller.signal })
      .then((response) => setPreviewStatus(response.ok ? 'ready' : 'error'))
      .catch((error) => {
        if (error.name !== 'AbortError') setPreviewStatus('error');
      });

    return () => controller.abort();
  }, [resource, viewUrl]);

  if (!resource?.fileUrl) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-viewer-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/15 bg-[#111827] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="rounded-xl bg-accentBlue/10 p-2 text-accentBlue">
              <FileText size={20} />
            </span>
            <div className="min-w-0">
              <h2 id="pdf-viewer-title" className="truncate text-sm font-bold text-white sm:text-base">
                {resource.title}
              </h2>
              <p className="truncate text-xs text-gray-400">
                {[resource.subject, resource.year].filter(Boolean).join(' · ') || 'PDF resource'}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {previewStatus === 'ready' && (
              <>
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-gray-200 transition-colors hover:bg-white/10"
                  title="Open PDF in a new tab"
                  aria-label="Open PDF in a new tab"
                >
                  <ExternalLink size={15} /> <span className="hidden sm:inline">Open</span>
                </a>
                <a
                  href={downloadUrl}
                  className="flex items-center gap-2 rounded-xl bg-accentBlue px-3 py-2 text-xs font-bold text-black transition-opacity hover:opacity-90"
                >
                  <Download size={15} />
                  <span className="hidden sm:inline">Download</span>
                </a>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/10 bg-white/5 p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close PDF viewer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 bg-gray-200">
          {previewStatus === 'loading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#111827] text-gray-300">
              <LoaderCircle size={30} className="animate-spin text-accentBlue" />
              <p className="text-sm">Loading PDF preview…</p>
            </div>
          )}
          {previewStatus === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#111827] px-6 text-center">
              <span className="rounded-2xl bg-red-500/10 p-4 text-red-400">
                <AlertCircle size={32} />
              </span>
              <div>
                <h3 className="font-bold text-white">PDF file is unavailable</h3>
                <p className="mt-1 max-w-md text-sm text-gray-400">
                  The saved resource record exists, but its PDF file is missing. An admin needs to replace the PDF from Admin Resources.
                </p>
              </div>
            </div>
          )}
          {previewStatus === 'ready' && (
            <iframe
              src={`${viewUrl}#toolbar=1&navpanes=0`}
              title={`${resource.title} PDF preview`}
              className="h-full w-full border-0"
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfViewerModal;
